import React, { useState } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    IconButton,
    TextField,
    Button,
    Paper,
    Link
} from '@mui/material';

// Icons
import CakeIcon from '@mui/icons-material/Cake';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublicIcon from '@mui/icons-material/Public';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useContactUpdate } from '../../../hooks/useContactUpdate';
import { contactDetailStyles, animations } from './styles';
import { getRhythmHelper, getRhythmLabel } from '../../../utils/relationshipRhythm';
import ContactLocation from './ContactLocation';

const ContactPersonalInfo = ({ 
    contact, 
    onContactUpdate, 
    onSnackbar, 
    t = {} 
}) => {
    // Используем унифицированный хук для обновления контактов
    const { updateContactPartial } = useContactUpdate();
    
    // States for editing
    const [editingNotes, setEditingNotes] = useState(false);
    const [editedNotes, setEditedNotes] = useState('');
    const [notesCollapsed, setNotesCollapsed] = useState(true);

    // Notes handlers
    const handleSaveNotes = async () => {
        try {
            const updatedContact = await updateContactPartial(contact._id, {
                notes: editedNotes
            }, contact);
            
            onContactUpdate(updatedContact);
            setEditingNotes(false);
            
            onSnackbar({
                open: true,
                message: 'Notes saved successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error saving notes:', error);
            onSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error saving notes',
                severity: 'error'
            });
        }
    };

    const handleCancelNotes = () => {
        setEditingNotes(false);
        setEditedNotes(contact.notes || '');
    };

    // Important dates handlers
    // Important dates and location are now handled by separate components

    return (
        <Box sx={{ ...contactDetailStyles.sectionContainer, ...animations.fadeInUp }}>
            <Typography variant="h6" sx={contactDetailStyles.sectionTitle}>
                {t.personalInfo || 'Personal Info'}
            </Typography>
            <List dense disablePadding>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                    alignItems: 'stretch',
                    mb: 1.5
                }}>
                    {/* Contact Frequency */}
                    <ListItem disablePadding sx={{ ...contactDetailStyles.listItem, height: '100%' }}>
                        <ListItemIcon>
                                <EventIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography variant="body2" sx={contactDetailStyles.itemTitle}>
                                    {contact.contactFrequency ? getRhythmLabel(contact.contactFrequency) : 'Not set'}
                                </Typography>
                            }
                            secondary={
                                <Typography variant="body2" sx={contactDetailStyles.itemMeta}>
                                    {contact.contactFrequency ? getRhythmHelper(contact.contactFrequency) : (t.contactFrequency || 'Contact Frequency')}
                                </Typography>
                            }
                        />
                    </ListItem>

                    <ContactLocation
                        contact={contact}
                        onContactUpdate={onContactUpdate}
                        t={t}
                        compact
                    />
                </Box>
                
                {/* Birthday */}
                {contact.birthday && (
                    <ListItem disablePadding sx={contactDetailStyles.listItem}>
                        <ListItemIcon>
                                <CakeIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                            primary={
                                <Typography variant="body2" sx={contactDetailStyles.itemTitle}>
                                    {contact.birthday}
                                </Typography>
                            } 
                            secondary={
                                <Typography variant="body2" sx={contactDetailStyles.itemMeta}>
                                    {t.birthday || 'Birthday'}
                                </Typography>
                            } 
                        />
                    </ListItem>
                )}
                
                {/* Important dates are now handled by ContactImportantDates component */}
                
                {/* Addresses */}
                {contact.addresses && Array.isArray(contact.addresses) && contact.addresses.length > 0 && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={contactDetailStyles.subsectionLabel}>
                            {t.addresses || 'Addresses'}
                        </Typography>
                        <List disablePadding>
                            {contact.addresses.map((addressObj, idx) => {
                                const addressParts = [];
                                if (addressObj.street) addressParts.push(addressObj.street);
                                if (addressObj.structuredCity) addressParts.push(addressObj.structuredCity);
                                if (addressObj.state) addressParts.push(addressObj.state);
                                if (addressObj.postalCode) addressParts.push(addressObj.postalCode);
                                if (addressObj.structuredCountry) addressParts.push(addressObj.structuredCountry);
                                
                                const fullAddress = addressParts.join(', ');
                                const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
                                
                                return (
                                    <ListItem key={`address-${idx}`} disablePadding sx={{ ...contactDetailStyles.listItem, pl: 1 }}>
                                        <ListItemIcon sx={{ minWidth: '36px' }}>
                                                <PublicIcon fontSize="small" color={addressObj.isPrimary ? "primary" : "action"} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <span style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}>
                                                    <Link href={mapUrl} target="_blank" rel="noopener">
                                                        {fullAddress}
                                                    </Link>
                                                    {addressObj.isPrimary && (
                                                        <span style={{
                                                            fontSize: '0.7rem',
                                                            background: 'primary.main',
                                                            color: 'white',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px'
                                                        }}>
                                                            {t.primary || "Primary"}
                                                        </span>
                                                    )}
                                                </span>
                                            } 
                                            secondary={
                                                <Typography variant="body2" sx={contactDetailStyles.itemMeta}>
                                                    {addressObj.label || (t.address || 'Address')}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                )}
                
                {/* Notes section */}
                <ListItem disablePadding sx={{ mt: 2 }}>
                    <ListItemText 
                        primary={
                            <span style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center' 
                            }}>
                                <Typography component="span" sx={contactDetailStyles.subsectionLabel}>
                                    {t.notes || 'Notes'}
                                </Typography>
                                {!editingNotes && (
                                    <span style={{ display: 'inline-flex', gap: 4 }}>
                                        <IconButton
                                            size="small"
                                            sx={contactDetailStyles.inlineAction}
                                            title={notesCollapsed ? 'Show notes' : 'Hide notes'}
                                            onClick={() => setNotesCollapsed((current) => !current)}
                                        >
                                            {notesCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                                        </IconButton>
                                        <IconButton 
                                            size="small" 
                                            sx={contactDetailStyles.inlineAction}
                                            title="Edit notes"
                                            onClick={() => {
                                                setEditingNotes(true);
                                                setNotesCollapsed(false);
                                                setEditedNotes(contact.notes || '');
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                )}
                            </span>
                        } 
                        secondary={
                            editingNotes ? (
                                <span style={{ 
                                    display: 'block',
                                    marginTop: '0.5rem'
                                }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={editedNotes}
                                        onChange={(e) => setEditedNotes(e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        placeholder="Add notes about this contact..."
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            onClick={handleSaveNotes}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancelNotes}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                </span>
                            ) : notesCollapsed ? null : (
                                <Typography component="span" sx={{ ...contactDetailStyles.bodyText, ...contactDetailStyles.noteBlock }}>
                                    {contact.notes || 'No notes added'}
                                </Typography>
                            )
                        } 
                    />
                </ListItem>
            </List>
        </Box>
    );
};

export default ContactPersonalInfo; 
